import { createFileRoute } from '@tanstack/react-router';
import { AtSign, Fingerprint, User2 } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/_authenticated/app')({
  component: AppHomePage,
});

type InfoRow = {
  label: string;
  value: string | undefined;
  icon: typeof User2;
};

function AppHomePage() {
  const { user } = useAuth();

  const rows: InfoRow[] = [
    { label: 'Username', value: user?.username, icon: User2 },
    { label: 'Email', value: user?.email ?? undefined, icon: AtSign },
    { label: 'User ID', value: user?.id, icon: Fingerprint },
  ];

  const initial = (user?.displayName ?? user?.username ?? '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div className="relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-primary/15 blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-8">
        <section className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="relative">
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-gradient-to-br from-primary to-primary/50 text-primary-foreground flex items-center justify-center text-3xl font-bold shadow-lg shadow-primary/20">
              {initial}
            </div>
            <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-card bg-primary" />
          </div>
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Signed in
            </p>
            <h1 className="text-2xl sm:text-3xl font-bold break-words">
              Welcome back, {user?.displayName}
            </h1>
            <p className="text-sm text-muted-foreground">
              This area is only visible to authenticated users.
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-border/60 bg-card/70 backdrop-blur-sm overflow-hidden">
          <div className="px-6 sm:px-8 py-5 border-b border-border/60">
            <h2 className="font-semibold">Your account</h2>
            <p className="text-sm text-muted-foreground">
              Details from your Google profile
            </p>
          </div>
          <dl className="divide-y divide-border/60">
            {rows.map(({ label, value, icon: Icon }) => (
              <div
                key={label}
                className="grid grid-cols-[auto_1fr] items-center gap-4 px-6 sm:px-8 py-4"
              >
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                    {label}
                  </dt>
                  <dd className="text-sm sm:text-base break-words">
                    {value ?? '—'}
                  </dd>
                </div>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </div>
  );
}
